import { supabase } from '../supabase';
import type { OrderWithdrawal, RefundedVia, WithdrawalStatus } from '../withdrawals';

const time = (v: string | null | undefined): number | undefined => (v ? new Date(v).getTime() : undefined);
const num = (v: unknown): number | undefined => (v === null || v === undefined ? undefined : Number(v));

const mapWithdrawal = (r: any): OrderWithdrawal => ({
    id: r.id,
    receivedAt: time(r.received_at) || Date.now(),
    orderNumber: r.order_number,
    customerName: r.customer_name,
    customerEmail: r.customer_email || undefined,
    message: r.message || undefined,
    orderId: r.order_id || undefined,
    classId: r.class_id || undefined,
    paymentMethod: r.payment_method || undefined,
    deliveryMethod: r.delivery_method || undefined,
    orderTotal: num(r.order_total),
    wasMarkedPaid: r.was_marked_paid === true,
    status: r.status as WithdrawalStatus,
    refundAmount: num(r.refund_amount),
    refundedVia: (r.refunded_via || undefined) as RefundedVia | undefined,
    orderTrashed: r.order_trashed === true,
    reminderSentAt: time(r.reminder_sent_at),
    note: r.note || undefined,
    resolvedAt: time(r.resolved_at),
});

/** Kolumnerna inloggade får läsa (aldrig kundens e-post eller meddelande). */
const GROUP_COLUMNS = 'id, received_at, order_number, customer_name, order_id, class_id, order_total, was_marked_paid, status, refund_amount, refunded_via, resolved_at';

/**
 * Admins lista med kunduppgifter. Innan databasen har funktionen (migreringen
 * inte körd) blir listan tom i stället för ett fel, så att Ordrar fungerar som förut.
 */
export const getWithdrawals = async (): Promise<OrderWithdrawal[]> => {
    const { data, error } = await supabase.rpc('list_order_withdrawals');
    if (error) return [];
    return ((data as any[]) || []).map(mapWithdrawal);
};

/**
 * Ångrade köp för den inloggades ordrar (säljaren sina, den ansvariga
 * gruppens, admin alla). Kundens telefon hämtas från ordern för dem gruppen
 * ska betala tillbaka till. Tom lista utan tabellen.
 */
export const getMyWithdrawals = async (): Promise<(OrderWithdrawal & { customerPhone?: string })[]> => {
    const { data, error } = await supabase.from('order_withdrawals').select(GROUP_COLUMNS).order('received_at', { ascending: false });
    if (error || !data) return [];
    const list = (data as any[]).map(mapWithdrawal);
    const ids = list.filter(w => w.status === 'AWAITING_REFUND' && w.orderId).map(w => w.orderId!);
    if (ids.length === 0) return list;
    const { data: orders } = await supabase.from('orders').select('id, customer_phone').in('id', ids);
    const phone = new Map(((orders as any[]) || []).map(o => [o.id, o.customer_phone]));
    return list.map(w => (w.orderId && phone.get(w.orderId) ? { ...w, customerPhone: phone.get(w.orderId) } : w));
};

/** Säljaren eller den ansvariga: kunden har fått tillbaka pengarna. */
export const confirmWithdrawalRefund = async (id: string): Promise<OrderWithdrawal> => {
    const { data, error } = await supabase.rpc('confirm_withdrawal_refund', { p_id: id });
    if (error) throw new Error(error.message);
    return mapWithdrawal(data);
};

/** Admins beslut. Databasen lägger ordern i papperskorgen eller tar tillbaka den. */
export const resolveWithdrawal = async (id: string, status: WithdrawalStatus, refundAmount?: number, note?: string): Promise<OrderWithdrawal> => {
    const { data, error } = await supabase.rpc('resolve_order_withdrawal', {
        p_id: id,
        p_status: status,
        p_refund_amount: refundAmount ?? null,
        p_note: note ?? null,
    });
    if (error) throw new Error(error.message);
    return mapWithdrawal(data);
};
